/* @jsx h */
import { useMenu } from "@app/vue/providers/index";
import { calculateCartSubtotal, formatPrice } from "@lemmonhq/orders";

import { computed, defineComponent, h } from "vue";
import { getDeliveryCities } from "@lemmonhq/blogic";
import { useLocalization } from "@lemmonhq/uikit";

export const BillSection = defineComponent({
  setup() {
    const { cart, shop } = useMenu();
    const { t } = useLocalization();

    const subtotal = computed(() => calculateCartSubtotal(cart.cart));
    const tips = computed(() =>
      cart.cart.tips?.value ? cart.cart.tips.value : 0,
    );
    const deliveryFee = computed(() =>
      cart.cart.deliveryFee?.value ? cart.cart.deliveryFee.value : 0,
    );
    const total = computed(() => {
      return subtotal.value + tips.value + deliveryFee.value;
    });

    const minimumOrderInfo = computed(() => {
      if (
        cart.cart.serviceMethod !== "delivery" ||
        !cart.cart.selectedAddress ||
        !shop.value
      ) {
        return null;
      }

      const deliveryCities = getDeliveryCities(shop.value.settings || []);
      const selectedCity = deliveryCities.find(
        (city) =>
          city.name.toLowerCase() ===
          cart.cart.selectedAddress?.city.toLowerCase(),
      );

      if (!selectedCity || !selectedCity.minimum) {
        return null;
      }

      const minimumAmount = parseFloat(selectedCity.minimum);
      const difference = minimumAmount - subtotal.value;

      return {
        minimum: minimumAmount,
        difference: difference > 0 ? difference : 0,
        isMet: difference <= 0,
        city: selectedCity.name,
      };
    });

    const currency = computed(() => shop.value?.currency || "CHF");

    return () => (
      <div class="p-2 ">
        <h3 class="text-lg font-semibold mb-4">
          {t("customer.checkout.bill_details")}
        </h3>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span>{t("customer.checkout.subtotal")}</span>
            <span>{formatPrice(subtotal.value, true, 2, currency.value)}</span>
          </div>
          {tips.value ? (
            <div class="flex justify-between">
              <span>{t("customer.checkout.tips")}</span>
              <span>{formatPrice(tips.value, true, 2, currency.value)}</span>
            </div>
          ) : null}
          {deliveryFee.value ? (
            <div class="flex justify-between">
              <span>
                {t("customer.checkout.delivery_fee", [
                  cart.cart.deliveryFee?.label || "",
                ])}
              </span>
              <span>
                {formatPrice(deliveryFee.value, true, 2, currency.value)}
              </span>
            </div>
          ) : null}

          <div class="flex justify-between font-semibold text-lg pt-2 ">
            <span>{t("customer.checkout.total")}</span>
            <span>{formatPrice(total.value, true, 2, currency.value)}</span>
          </div>
        </div>

        {/* Minimum Order Alert - UberEats/DoorDash Style */}
        {minimumOrderInfo.value && !minimumOrderInfo.value.isMet && (
          <div class="mt-4 p-4 bg-white border border-red-200 rounded-xl shadow-sm">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <span class="material-symbols-outlined text-red-500 text-lg">
                    shopping_cart
                  </span>
                </div>
                <div>
                  <div class="font-semibold text-gray-900 text-sm">
                    {t("customer.checkout.add_to_reach_minimum", [
                      formatPrice(
                        minimumOrderInfo.value.difference,
                        true,
                        2,
                        currency.value,
                      ),
                    ])}
                  </div>
                  <div class="text-xs text-gray-500 mt-0.5">
                    {t("customer.checkout.minimum_for_delivery", [
                      formatPrice(
                        minimumOrderInfo.value.minimum,
                        true,
                        2,
                        currency.value,
                      ),
                      minimumOrderInfo.value.city,
                    ])}
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div class="text-xs text-gray-400 font-medium">
                  {formatPrice(subtotal.value, true, 2, currency.value)} /{" "}
                  {formatPrice(
                    minimumOrderInfo.value.minimum,
                    true,
                    2,
                    currency.value,
                  )}
                </div>
                <div class="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
                  <div
                    class="h-full bg-red-400 rounded-full transition-all duration-300"
                    style={`width: ${Math.min(
                      (subtotal.value / minimumOrderInfo.value.minimum) * 100,
                      100,
                    )}%`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success message when minimum is met */}
        {minimumOrderInfo.value && minimumOrderInfo.value.isMet && (
          <div class="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
            <div class="flex items-center">
              <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <span class="material-symbols-outlined text-green-600 text-sm">
                  check
                </span>
              </div>
              <span class="text-sm text-green-700 font-medium">
                {t("customer.checkout.minimum_order_met")}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  },
});
